import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import './Navbar.css';
import API_BASE_URL from '../api';

function Navbar({ handleLogout, isAdminLoggedIn, toggleSidebar, sidebarOpen }) {
    const [categories, setCategories] = useState([]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [searchText, setSearchText] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const isAdminPage = location.pathname.startsWith('/admin');

    const closeMobileMenu = () => setIsMenuOpen(false);
    const toggleMobileMenu = () => setIsMenuOpen(prev => !prev);

    useEffect(() => {
        fetchCategories();
        const handleScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/categories`);
            setCategories(structureCategories(response.data || []));
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const structureCategories = (cats) => {
        const enabled = cats.filter(c => c.enabled);
        const map = {};
        const roots = [];
        enabled.forEach(c => { c.children = []; map[c.id] = c; });
        enabled.forEach(c => {
            if (c.parent_id) { const p = map[c.parent_id]; if (p) p.children.push(c); }
            else roots.push(c);
        });
        Object.values(map).forEach(c => c.children.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
        roots.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        return roots;
    };

    const handleGlobalSearch = (e) => {
        e.preventDefault();
        const q = searchText.trim();
        if (!q) return;
        closeMobileMenu();
        navigate(`/search?q=${encodeURIComponent(q)}`);
    };

    const navClass = `navbar1${isAdminLoggedIn && sidebarOpen ? ' navbar-pushed' : ''}`;

    return (
        <nav className={navClass}>

            {/* ── MOBILE ADMIN BAR ── */}
            {isAdminLoggedIn && (
                <div className="admin-mobile-bar">
                    <button type="button" className="admin-mobile-burger" onClick={toggleSidebar} aria-label="Toggle admin sidebar">
                        <i className="fas fa-bars" />
                    </button>
                    <span className="admin-mobile-label">Admin</span>
                    <Link to="/admin/dashboard" className={`admin-mobile-profile${isAdminPage ? ' admin-profile-icon-white' : ''}`} aria-label="Admin panel">
                        <i className="fas fa-user-circle" />
                    </Link>
                </div>
            )}

            {/* ── DESKTOP TOP RED BAR ── */}
            <div className="logo-bar">
                {isAdminLoggedIn && (
                    <button type="button" className="admin-burger" onClick={toggleSidebar} aria-label="Toggle admin sidebar">
                        <i className="fas fa-bars" />
                    </button>
                )}
                <div className="logo-bar-inner">
                    <div className="logo-bar-right">
                        <div className="header-actions">
                            <div className="contact-buttons">
                                <a href="tel:07970506155" className="contact-btn contact-btn-call">
                                    <i className="fas fa-phone-alt" /><span>CALL 07970 506155</span>
                                </a>
                                <a href="https://wa.me/" className="contact-btn contact-btn-whatsapp" target="_blank" rel="noopener noreferrer">
                                    <i className="fab fa-whatsapp" /><span>WHATSAPP ENQUIRY</span>
                                </a>
                            </div>
                            {isAdminLoggedIn && (
                                <Link to="/admin/dashboard" className={`admin-profile-icon${isAdminPage ? ' admin-profile-icon-white' : ''}`} aria-label="Admin panel">
                                    <i className="fas fa-user-circle" />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── WHITE BOTTOM NAV ── */}
            <div className={`Bottom-nav-bar${scrolled ? ' scrolled' : ''}`}>

                {/* ── Logo + burger row — always on one line, never part of the menu ── */}
                <div className="Bottom-nav-bar-container">
                    <Link to="/" className="bottom-navbar-logo" onClick={closeMobileMenu}>
                        <img src="/images/PloughBreakersLogo.svg" alt="Logo" />
                    </Link>

                    {/* Desktop nav menu sits here inline */}
                    <ul className="nav-menu nav-menu-desktop">
                        {categories.map(category => (
                            <li key={category.id} className={`nav-item-Bottom ${category.children?.length ? 'has-children' : ''}`}>
                                <NavLink to={`/category/${category.id}`} className={({ isActive }) => `nav-links_bottom${isActive ? ' nav-active' : ''}`} onClick={closeMobileMenu}>
                                    <div className="category-item">
                                        {category.image_url && <img src={category.image_url} alt={category.name} className="category-image" />}
                                        <span className="category-label">{category.name}</span>
                                    </div>
                                </NavLink>
                                {category.children?.length > 0 && (
                                    <ul className="dropdown">
                                        {category.children.map(child => (
                                            <li key={child.id} className="dropdown-item">
                                                <NavLink to={`/category/${child.id}`} className={({ isActive }) => `dropdown-link${isActive ? ' nav-active' : ''}`} onClick={closeMobileMenu}>{child.name}</NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                        <li className="nav-item-Bottom">
                            <NavLink to="/about" className={({ isActive }) => `nav-links_bottom${isActive ? ' nav-active' : ''}`} onClick={closeMobileMenu}>
                                <div className="category-item"><span className="category-label">About</span></div>
                            </NavLink>
                        </li>
                        <li className="nav-item-Bottom">
                            <NavLink to="/contact" className={({ isActive }) => `nav-links_bottom${isActive ? ' nav-active' : ''}`} onClick={closeMobileMenu}>
                                <div className="category-item"><span className="category-label">Contact</span></div>
                            </NavLink>
                        </li>
                    </ul>

                    {/* Mobile burger — only visible on mobile */}
                    <button type="button" className="menu-icon" onClick={toggleMobileMenu} aria-label="Toggle navigation">
                        <i className={isMenuOpen ? 'fas fa-times' : 'fas fa-bars'} />
                    </button>
                </div>

                {/* ── Mobile dropdown menu — separate from logo/burger row ── */}
                {isMenuOpen && (
                    <ul className="nav-menu-mobile">
                        {categories.map(category => (
                            <li key={category.id} className="nav-item-mobile">
                                <NavLink to={`/category/${category.id}`} className={({ isActive }) => `nav-links_bottom${isActive ? ' nav-active' : ''}`} onClick={closeMobileMenu}>
                                    <div className="category-item">
                                        {category.image_url && <img src={category.image_url} alt={category.name} className="category-image-mobile" />}
                                        <span className="category-label">{category.name}</span>
                                    </div>
                                </NavLink>
                            </li>
                        ))}
                        <li className="nav-item-mobile">
                            <NavLink to="/about" className={({ isActive }) => `nav-links_bottom${isActive ? ' nav-active' : ''}`} onClick={closeMobileMenu}>
                                <div className="category-item"><span className="category-label">About</span></div>
                            </NavLink>
                        </li>
                        <li className="nav-item-mobile">
                            <NavLink to="/contact" className={({ isActive }) => `nav-links_bottom${isActive ? ' nav-active' : ''}`} onClick={closeMobileMenu}>
                                <div className="category-item"><span className="category-label">Contact</span></div>
                            </NavLink>
                        </li>
                    </ul>
                )}

                {/* ── SEARCH STRIP ── */}
                <div className="nav-search-strip">
                    <form className="navbar-search" onSubmit={handleGlobalSearch}>
                        <input type="text" placeholder="Search products..." aria-label="Search" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                        <button type="submit" aria-label="Search"><i className="fas fa-search" /></button>
                    </form>
                </div>

            </div>

        </nav>
    );
}

export default Navbar;